import DatePickerInput from 'components/DateInput'
import { createModal } from 'components/Modals/ModalFactory'
import { ModalWrapper } from 'components/Modals/ModalWrapper'
import Translation from 'components/Translation'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { updateMembershipClient } from 'services/memberships-client'
import { MembershipApiItem } from 'types/membership'
import {
    MembershipClient,
    MembershipClientDTO,
} from 'types/membership-client'
import { ServiceApiItem } from 'types/service'
import { classNames } from 'utils/dom-helpers'
import { useApiRequest, useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { GeneralFormValues } from '../types'
import MembershipReactivateModal from './MembershipReactivateModal'
import MembershipCancelModal from './MembershipCancelModal'
import { getTranslation } from 'utils/language-helper'
import { useAppContext } from 'context/AppContext'
import InputSpinner from 'components/Spinners/input'
import { useTenantContext } from 'context/TenantContext'
import CardSetupForm from './CardSetupForm'
import { useAppErrorApiContext } from 'context/AppErrorContext'

function RenderEligibleServices(props: any) {
    const list_items = props.eligible_services.map((item: string) => (
        <li key={item}>
            <i
                className="feather-check mr-2 text-green-500"
                aria-hidden="true"
            />
            {item}
        </li>
    ))

    return <ul className="columns-2 text-sm">{list_items}</ul>
}

const ModalProvider = createModal(
    AuthContext,
    'MembershipEditModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <Translation
                render_as="span"
                className="circular"
                content_key="edit_membership"
            />
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-x " />
        </span>
    )
)

export const MembershipEditModalOpener = ModalProvider.Opener
export const MembershipEditModalCloser = ModalProvider.Closer

export function MembershipEditModal() {
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const { translations, setGlobalLoading } = useAppContext()
    const { CreateClientModal, MembershipEditModal } = useAuth()
    const { tenant } = useTenantContext()
    const { attributes, setAttributes } = MembershipEditModal
    const [connected_account_id, setConnectedAccountId] = useState<string>()
    const [payment_info, setPaymetInfo] = useState<string>()
    const [services, setServices] = useState<string[]>([])

    const remaining_field_ref = useRef<HTMLInputElement | null>(null)
    const [date_picker, toggleDatePicker] = useState(false)

    const [show_payment_form, togglePaymentForm] = useState(false)
    const [show_reactivate_modal, toggleReactivateModal] = useState(false)
    const [show_cancel_modal, toggleCancelModal] = useState(false)

    const membership = attributes?.selected_membership as MembershipApiItem
    const client_id = CreateClientModal.attributes?.id as string

    const is_cancelled = attributes?.status != 'active'

    const { data: services_api_response } = useFetch(
        `/v1/services?tenantId=${tenant?.id}&size=500&isDeleted=false`,
        FetchMethods.GET,
        !!tenant?.id && MembershipEditModal.is_open
    )

    const { data: integration_api_response } = useFetch(
        `/v1/tenant-integrations/tenant-id?tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!tenant?.id && MembershipEditModal.is_open
    )

    const { handleSubmit, reset } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        if (MembershipEditModal.is_open) {
            setPaymetInfo('')
            togglePaymentForm(false)
        }
    }, [MembershipEditModal.is_open])

    useEffect(() => {
        if (services_api_response.content && membership) {
            let categoryIds = membership?.categoryIds || []
            let serviceIds = membership?.serviceIds || []
            let selected_services: string[] = []
            services_api_response.content.map((service: ServiceApiItem) => {
                if (
                    categoryIds.indexOf(service.category.id) > -1 ||
                    serviceIds.indexOf(service.id) > -1
                ) {
                    let service_detail = `${service.name} (${service.duration} min)`
                    selected_services.push(service_detail)
                }
            })
            setServices(selected_services)
        }
    }, [services_api_response, membership])

    useEffect(() => {
        if (
            integration_api_response?.content?.length &&
            integration_api_response?.content &&
            tenant?.id &&
            client_id
        ) {
            let stripe_integration = integration_api_response?.content[0]
            if (
                stripe_integration &&
                stripe_integration.connectedAccountId
            ) {
                setConnectedAccountId(stripe_integration.connectedAccountId)
                let api = getApiRequest(
                    `/v1/payments/client-payment-method?tenantId=${tenant?.id}&clientId=${client_id}`
                )
                api.then((payment_api_response) => {
                    if (payment_api_response?.cardLast4) {
                        setPaymetInfo(
                            getTranslation('card_ending', translations, {
                                last4: payment_api_response?.cardLast4,
                            })
                        )
                    } else {
                        setPaymetInfo(
                            getTranslation(
                                'none',
                                translations
                            ).toUpperCase()
                        )
                    }
                })
            }
        }
    }, [integration_api_response, tenant?.id, client_id])

    function handleClose() {
        reset()
        MembershipEditModal.close()
    }

    function handleStatusClick() {
        if (is_cancelled) {
            toggleReactivateModal(true)
        } else {
            toggleCancelModal(true)
        }
    }

    const onSubmit = async () => {
        if (!attributes) return

        try {
            setGlobalLoading(true)
            const membership_client_id = attributes.id as string
            let dto: MembershipClientDTO = {
                id: attributes.id as string,
                clientId: attributes.clientId as string,
                expiresAt: attributes.expiresAt as string,
                membershipId: attributes.membershipId as string,
                renewsAt: attributes.expiresAt as string,
                startsAt: attributes.startsAt as string,
                usedAmount: attributes.usedAmount as number,
                usedMinutes: attributes.usedMinutes as number,
                usedServiceCount: attributes.usedServiceCount as number,
            }

            if (
                attributes.expiresAt != attributes.renewal_or_expiry_date ||
                attributes.renewsAt != attributes.renewal_or_expiry_date
            ) {
                dto.expiresAt = attributes.renewal_or_expiry_date as string
                dto.renewsAt = attributes.renewal_or_expiry_date as string
            }

            if (
                attributes.new_remaining &&
                attributes.remaining != attributes.new_remaining
            ) {
                const diff =
                    (attributes.new_remaining as number) -
                    (attributes.remaining as number)
                switch (attributes.redemption_type) {
                    case 'Amount':
                        dto.usedAmount =
                        parseFloat(((attributes.usedAmount as number) - diff).toFixed(2))
                        dto.usedMinutes = 0
                        dto.usedServiceCount = 0
                        break
                    case 'Minutes':
                        dto.usedMinutes =
                            (attributes.usedMinutes as number) - diff
                        dto.usedAmount = 0
                        dto.usedServiceCount = 0
                        break
                    case 'Services':
                        dto.usedServiceCount =
                            (attributes.usedServiceCount as number) - diff
                        dto.usedAmount = 0
                        dto.usedMinutes = 0
                        break
                }
            }

            let api = await updateMembershipClient(
                membership_client_id,
                dto
            )
            if (api.ok) {
                setAttributes({
                    refetch: true,
                })
                MembershipEditModal.close()
            }
            else {
                throw new Error(api.message)
            }
        } catch (err) {
            setGlobalError(err)
        }
        finally {
            setGlobalLoading(false)
        }
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-auto">
                <div className="bg-white shadow-2xl overflow-x-hidden sm:rounded-md relative border-2 border-gray-100 my-auto">
                    <div className="flex justify-between text-lg text-primary-dark px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <Translation
                            render_as="span"
                            className="circular ml-3"
                            content_key="edit_membership"
                        />
                        <MembershipEditModalCloser className="pt-1" />
                    </div>
                    <div className="flex flex-col gap-y-10 w-full p-10 text-black">
                        <form
                            method="POST"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <div className="lg:flex">
                                <fieldset className="pb-6 lg:w-2/3 lg:pr-8">
                                    <label
                                        htmlFor="select_membership"
                                        className="block text-lg truncate"
                                    >
                                        <Translation content_key="select_membership" />
                                    </label>
                                    <input
                                        type="text"
                                        disabled={true}
                                        id="select_membership"
                                        className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md"
                                        defaultValue={`${
                                            attributes?.name as string
                                        } (${attributes?.location})`}
                                    />
                                </fieldset>
                                <fieldset className="pb-6 lg:w-1/3">
                                    <label
                                        htmlFor="membership_type"
                                        className="block text-lg truncate"
                                    >
                                        <Translation content_key="membership_type" />
                                    </label>
                                    <input
                                        type="text"
                                        disabled={true}
                                        id="membership_type"
                                        className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 bg-green-100 rounded-md uppercase"
                                        defaultValue={getTranslation(
                                            attributes?.type as string,
                                            translations
                                        )}
                                    />
                                </fieldset>
                            </div>

                            <div className="lg:flex">
                                <fieldset className="pb-6 lg:w-1/4 lg:pr-8">
                                    <label
                                        htmlFor="details"
                                        className="block text-lg truncate"
                                    >
                                        <Translation content_key="payment_information" />
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            disabled={true}
                                            id="details"
                                            className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md"
                                            value={payment_info}
                                        />
                                        <InputSpinner className="absolute top-4 right-3" loading={!payment_info} />
                                        <i
                                            className={classNames(
                                                "feather-edit text-red-800 absolute top-4 right-3 cursor-pointer",
                                                !payment_info ? 'hidden' : ''
                                            )}
                                            onClick={() =>
                                                togglePaymentForm(
                                                    !show_payment_form
                                                )
                                            }
                                        />
                                    </div>
                                </fieldset>
                                <fieldset className="pb-6 lg:w-1/4 lg:pr-8">
                                    <label
                                        htmlFor="amount_remaining"
                                        className="block text-lg truncate"
                                    >
                                        <Translation
                                            content_key="remaining_value"
                                            content_values={{
                                                value: getTranslation(
                                                    attributes?.redemption_type as string,
                                                    translations
                                                ),
                                            }}
                                        />
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="remaining"
                                            ref={remaining_field_ref}
                                            type={attributes?.redemption_type == "Amount" ? "decimal" : "number"}
                                            min={0}
                                            className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md"
                                            style={{
                                                border: '1px solid gray',
                                            }}
                                            defaultValue={
                                                attributes?.remaining as number
                                            }
                                            onChange={(e) => {
                                                setAttributes({
                                                    ...attributes,
                                                    new_remaining:
                                                        e.target.value,
                                                })
                                            }}
                                            disabled={is_cancelled}
                                        />
                                        <i
                                            className={classNames(
                                                'feather-edit text-red-800 absolute top-4 right-3 cursor-pointer',
                                                is_cancelled ? 'hidden' : ''
                                            )}
                                            onClick={() => {
                                                remaining_field_ref.current?.focus()
                                            }}
                                        />
                                    </div>
                                </fieldset>
                                <fieldset className="pb-6 lg:w-1/4 lg:pr-8">
                                    <label
                                        htmlFor="renewal_or_expiry_date"
                                        className="block text-lg truncate"
                                    >
                                        <Translation
                                            content_key={
                                                attributes?.type ==
                                                'package'
                                                    ? 'expires_on'
                                                    : 'renews_on'
                                            }
                                        />
                                    </label>
                                    {attributes?.renewal_or_expiry_date ? (
                                        <div className="relative">
                                            <DatePickerInput
                                                className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md calendar"
                                                selected={
                                                    new Date(
                                                        attributes?.renewal_or_expiry_date as Date
                                                    )
                                                }
                                                onChange={(
                                                    date: Date | null
                                                ) => {
                                                    setAttributes({
                                                        ...attributes,
                                                        renewal_or_expiry_date:
                                                            date as Date,
                                                    })
                                                    toggleDatePicker(
                                                        !date_picker
                                                    )
                                                }}
                                                onInputClick={() => {
                                                    toggleDatePicker(
                                                        !date_picker
                                                    )
                                                }}
                                                open={date_picker}
                                                disabled={is_cancelled}
                                                onClickOutside={() => {
                                                    toggleDatePicker(
                                                        !date_picker
                                                    )
                                                }}
                                            />
                                            <i
                                                className={classNames(
                                                    'feather-edit text-red-800 absolute top-5 right-3 cursor-pointer',
                                                    is_cancelled
                                                        ? 'hidden'
                                                        : ''
                                                )}
                                                onClick={() => {
                                                    toggleDatePicker(
                                                        !date_picker
                                                    )
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            id="renewal_or_expiry_date"
                                            className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md"
                                            defaultValue={
                                                attributes?.renewal as string
                                            }
                                        />
                                    )}
                                </fieldset>
                                <fieldset className="pb-6 lg:w-1/4">
                                    <label
                                        htmlFor="membership_status"
                                        className="block text-lg truncate"
                                    >
                                        <Translation content_key="membership_status" />
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            disabled={true}
                                            id="membership_status"
                                            className="px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md capitalize"
                                            defaultValue={getTranslation(
                                                attributes?.status as string,
                                                translations
                                            )}
                                        />
                                        <i
                                            className="feather-edit text-red-800 absolute top-4 right-3 cursor-pointer"
                                            onClick={handleStatusClick}
                                        />
                                    </div>
                                </fieldset>
                            </div>

                            {show_payment_form && connected_account_id && (
                                <CardSetupForm 
                                    closeForm={(card_info: string) => {
                                        if (card_info) {
                                            setPaymetInfo(card_info)
                                        }
                                        togglePaymentForm(false)
                                    }} 
                                />
                            )}

                            <div className="text-lg mt-3">
                                <fieldset className="block pb-6">
                                    <label className="block text-lg">
                                        <Translation content_key="eligible_services" />
                                    </label>
                                </fieldset>
                                <div className="block pb-6 w-full lg:w-2/3">
                                    <RenderEligibleServices
                                        eligible_services={services}
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="reset"
                                        onClick={handleClose}
                                        className="border-gray-200 rounded-lg border px-10"
                                    >
                                        <Translation content_key="cancel" />
                                    </button>
                                    <button
                                        type="submit"
                                        className={classNames(
                                            'group relative flex justify-center',
                                            'py-3 px-12 border border-transparent',
                                            'rounded-md text-white',
                                            'focus:outline-none',
                                            'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                        )}
                                    >
                                        <Translation content_key="save" />
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <MembershipReactivateModal
                    is_open={show_reactivate_modal}
                    onConfirm={() => {
                        toggleReactivateModal(false)
                        setAttributes({
                            refetch: true,
                        })
                        MembershipEditModal.close()
                    }}
                    onCancel={() => {
                        toggleReactivateModal(false)
                    }}
                />
                <MembershipCancelModal
                    is_open={show_cancel_modal}
                    membership_client={
                        { ...attributes } as MembershipClient
                    }
                    onConfirm={() => {
                        toggleCancelModal(false)
                        setAttributes({
                            refetch: true,
                        })
                        MembershipEditModal.close()
                    }}
                    onCancel={() => {
                        toggleCancelModal(false)
                    }}
                />
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default MembershipEditModal
